import { z } from 'zod';
import { dateToUTC } from 'shared/src/utils/zod/zod-utils';

export const ZohoCampaignId = z.object({
  zoho_campaign_id: z.string()
});
export type ZohoCampaignId = z.infer<typeof ZohoCampaignId>;

export const ProposalId = z.object({
  proposal_id: z.uuid()
});
export type ProposalId = z.infer<typeof ProposalId>;

export const StrategyId = z.object({
  strategy_id: z.string()
});
export type StrategyId = z.infer<typeof StrategyId>;

export const ExecutionId = z.object({
  execution_id: z.uuid()
});
export type ExecutionId = z.infer<typeof ExecutionId>;

export const LineItemId = z.object({
  line_item_id: z.uuid()
});
export type LineItemId = z.infer<typeof LineItemId>;

export const ZohoInsertionOrder = z.object({
  Modified_Time: dateToUTC,
  Created_Time: dateToUTC,
  IO_Type: z.string(),
  IO_Number: z.string(),
  id: z.string(),
  Name: z.string()
});
export type ZohoInsertionOrder = z.infer<typeof ZohoInsertionOrder>;

export const ZohoInsertionOrderResponse = z.object({
  data: z.array(ZohoInsertionOrder)
});
export type ZohoInsertionOrderResponse = z.infer<typeof ZohoInsertionOrderResponse>;

export const ZohoLookup = z.object({
  id: z.string(),
  name: z.string().nullish()
});
export type ZohoLookup = z.infer<typeof ZohoLookup>;

// `id` is the Zoho user id, which equals users.zoho_user_id.
export const ZohoOwnerLookup = z.object({
  id: z.string(),
  name: z.string().nullish(),
  email: z.string().nullish()
});
export type ZohoOwnerLookup = z.infer<typeof ZohoOwnerLookup>;

// A Zoho CRM Task. Picklist fields (Status/Priority/Type) are modeled as free
// strings so unseen option values never break parse. Campaign linkage: when
// `$se_module === 'Deals'`, `What_Id.id` is the Deal Id, which equals the Bravo Campaign id
// (campaigns.id). Unknown extra keys are tolerated (no .strict()).
export const ZohoTask = z.object({
  id: z.string(),
  Subject: z.string().nullish(),
  Description: z.string().nullish(),
  Status: z.string(),
  Priority: z.string().nullish(),
  Due_Date: z.string().nullish(),
  Owner: ZohoOwnerLookup,
  What_Id: ZohoLookup.nullish(),
  $se_module: z.string().nullish(),
  Who_Id: ZohoLookup.nullish(),
  Created_Time: dateToUTC.nullish(),
  Modified_Time: dateToUTC.nullish(),
  Closed_Time: dateToUTC.nullish(),
  Last_Activity_Time: dateToUTC.nullish(),
  Type: z.string().nullish(),
  Task_Type: z.string().nullish(),
  Working_Hours: z.number().nullish()
});
export type ZohoTask = z.infer<typeof ZohoTask>;

export const ZohoTaskSearchResponse = z.object({
  data: z.array(ZohoTask),
  info: z
    .object({
      per_page: z.number().nullish(),
      count: z.number().nullish(),
      page: z.number().nullish(),
      more_records: z.boolean().nullish()
    })
    .nullish()
});
export type ZohoTaskSearchResponse = z.infer<typeof ZohoTaskSearchResponse>;

export const ZohoCampaignStatus = z.union([
  z.literal('Potential Opportunity'),
  z.literal('New'),
  z.literal('Media Campaign Details'),
  z.literal('Avails/Plan Development'),
  z.literal('Proposal Sent'),
  z.literal('Book the Campaign'),
  z.literal('Traffic & Onboard'),
  z.literal('Pixel Setup'),
  z.literal('Campaign is Live'),
  z.literal('Campaign is Paused'),
  z.literal('Campaign Management & Reporting'),
  z.literal('Campaign Complete'),
  z.literal('Renew Campaign'),
  z.literal('Closed (Lost)'),
  z.literal('Exit - Dev Only'),
  z.literal('Campaign Approved to Launch'),
  z.literal('Campaign Cancelled'),
  z.literal('Pixel Check'),
  z.literal('Potential')
]);
export type ZohoCampaignStatus = z.infer<typeof ZohoCampaignStatus>;
export const zohoCampaignStatusValues = ZohoCampaignStatus.options.map(option => option.value);

export const ZohoAccountType = z.union([
  z.literal('Agency'),
  z.literal('Client Direct'),
  z.literal('Sub Account')
]);
export type ZohoAccountType = z.infer<typeof ZohoAccountType>;
export const zohoAccountTypeValues = ZohoAccountType.options.map(option => option.value);
